// src/components/modals/CreatePurchaseOrderModal.jsx
// Updated version with warehouse-specific item filtering and branch type restrictions
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Loader2, ChevronDown, Plus, Trash2, Search, Check, Package, AlertCircle } from 'lucide-react'
import ApiService from '../../services/api'
import PurchaseOrderService from '../../services/purchaseOrderService'
import ProductInventoryService from '../../services/productInventoryService'
import RawMaterialInventoryService from '../../services/rawMaterialInventoryService'
import { PurchaseOrderModel } from '../../utils/purchaseOrderModel'
import { PurchaseOrderUtils } from '../../utils/purchaseOrderUtils'

const CreatePurchaseOrderModal = ({
  isOpen,
  onClose,
  onOrderCreated,
  editOrder = null,
  mode = 'create' // 'create' or 'edit'
}) => {
  const [formData, setFormData] = useState({
    requestingBranch: '',
    supplierType: 'supplier',
    supplier: '',
    warehouse: '',
    orderDate: '',
    expectedDeliveryDate: '',
    notes: '',
    orderItems: []
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [dropdownsLoading, setDropdownsLoading] = useState(false)
  const [itemsLoading, setItemsLoading] = useState(false)

  // Dropdown data
  const [branches, setBranches] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [availableItems, setAvailableItems] = useState({
    rawMaterials: [],
    products: []
  })

  // Item selection states
  const [showItemSelector, setShowItemSelector] = useState(false)
  const [itemSearchTerm, setItemSearchTerm] = useState('')
  const [selectedItemType, setSelectedItemType] = useState('product')
  const [selectedItems, setSelectedItems] = useState([]) // For multiple selection
  const [selectorError, setSelectorError] = useState('') // Error shown in selector popup

  // Helper to get requesting branch details
  const getRequestingBranch = () => {
    return branches.find(branch => branch._id === formData.requestingBranch)
  }

  // Check if requesting branch is a warehouse
  const isRequestingBranchWarehouse = () => {
    const requestingBranch = getRequestingBranch()
    return requestingBranch?.type === 'warehouse'
  }

  // Get available supplier types based on requesting branch
  const getAvailableSupplierTypes = () => {
    if (isRequestingBranchWarehouse()) {
      // If requesting branch is a warehouse, only allow supplier
      return [{ value: 'supplier', label: 'Supplier' }]
    } else {
      // If requesting branch is not a warehouse, allow both
      return [
        { value: 'supplier', label: 'Supplier' },
        { value: 'warehouse', label: 'Warehouse' }
      ]
    }
  }

  // Get the branch ID to fetch items from
  const getItemSourceBranchId = () => {
    if (formData.supplierType === 'warehouse' && formData.warehouse) {
      // If supplier type is warehouse, get items from selected warehouse
      return formData.warehouse
    } else {
      // If supplier type is supplier, get items from requesting branch
      return formData.requestingBranch
    }
  }

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && editOrder) {
        // Populate form with existing order data
        setFormData({
          requestingBranch: editOrder.requestingBranch?._id || '',
          supplierType: editOrder.supplierType || 'supplier',
          supplier: editOrder.supplier?._id || '',
          warehouse: editOrder.warehouse?._id || '',
          orderDate: editOrder.orderDate ? editOrder.orderDate.split('T')[0] : '',
          expectedDeliveryDate: editOrder.expectedDeliveryDate ? editOrder.expectedDeliveryDate.split('T')[0] : '',
          notes: editOrder.notes || '',
          orderItems: editOrder.orderItems?.map(item => ({
            id: item._id || PurchaseOrderUtils.generateTempId(),
            itemType: item.itemType,
            productId: item.productId || '',
            rawMaterialId: item.rawMaterialId || '',
            quantity: item.quantity || 1,
            name: item.productSnapshot?.name || item.rawMaterialSnapshot?.name || '',
            code: item.productSnapshot?.sku || item.rawMaterialSnapshot?.code || '',
            unit: item.productSnapshot?.unit?.abbreviation || item.rawMaterialSnapshot?.unit?.abbreviation || ''
          })) || []
        })
      } else {
        // Reset form for create mode
        setFormData({
          requestingBranch: '',
          supplierType: 'supplier',
          supplier: '',
          warehouse: '',
          orderDate: new Date().toISOString().split('T')[0],
          expectedDeliveryDate: '',
          notes: '',
          orderItems: []
        })
      }
      setErrors({})
      fetchDropdownData()
    }
  }, [isOpen, editOrder, mode])

  // Reset selector states when opening
  useEffect(() => {
    if (showItemSelector) {
      setSelectedItems([])
      setSelectorError('')
      setItemSearchTerm('')
    }
  }, [showItemSelector])

  // Reset supplier type when requesting branch changes to warehouse
  useEffect(() => {
    if (formData.requestingBranch && isOpen) {
      const requestingBranch = branches.find(branch => branch._id === formData.requestingBranch)
      if (requestingBranch?.type === 'warehouse') {
        // If requesting branch is warehouse, force supplier type to be 'supplier'
        setFormData(prev => ({
          ...prev,
          supplierType: 'supplier',
          warehouse: '' // Clear warehouse selection
        }))
      }
    }
  }, [formData.requestingBranch, branches, isOpen])

  // Fetch available items when item source changes
  useEffect(() => {
    const sourceBranchId = getItemSourceBranchId()
    if (sourceBranchId && isOpen) {
      fetchBranchSpecificItems(sourceBranchId)
    } else {
      // Clear items when no valid source is selected
      setAvailableItems({
        rawMaterials: [],
        products: []
      })
    }
  }, [formData.requestingBranch, formData.supplierType, formData.warehouse, isOpen])

  // Fetch dropdown data using basic endpoints
  const fetchDropdownData = async () => {
    try {
      setDropdownsLoading(true)

      const promises = [
        ApiService.get('/branches').catch(() => ({ success: false, data: {} })),
        ApiService.get('/suppliers').catch(() => ({ success: false, data: {} }))
      ]

      const [branchesResponse, suppliersResponse] = await Promise.all(promises)

      // Handle branches
      if (branchesResponse?.success) {
        const allBranches = branchesResponse.data?.branches || []
        setBranches(allBranches)
        setWarehouses(allBranches.filter(branch => branch.type === 'warehouse'))
        console.log('✅ Loaded branches:', allBranches.length, '(warehouses:', allBranches.filter(b => b.type === 'warehouse').length, ')')
      }

      // Handle suppliers
      if (suppliersResponse?.success) {
        setSuppliers(suppliersResponse.data?.suppliers || [])
      }

    } catch (error) {
      console.error('Error fetching dropdown data:', error)
    } finally {
      setDropdownsLoading(false)
    }
  }

  // Fetch branch-specific available items
  const fetchBranchSpecificItems = async (branchId) => {
    if (!branchId) {
      setAvailableItems({ rawMaterials: [], products: [] })
      return
    }

    try {
      setItemsLoading(true)
      console.log('📦 Fetching branch-specific items for branch:', branchId)

      // Try to get inventory items specifically for purchase orders first
      let products = []
      let rawMaterials = []

      try {
        // Method 1: Try the dedicated purchase order inventory endpoint
        const inventoryResponse = await PurchaseOrderService.getInventoryItems(branchId)
        if (inventoryResponse?.success) {
          products = inventoryResponse.data?.products || []
          rawMaterials = inventoryResponse.data?.rawMaterials || []
          console.log('✅ Got items from purchase order inventory endpoint:', products.length, 'products,', rawMaterials.length, 'raw materials')
        }
      } catch (error) {
        console.log('⚠️ Purchase order inventory endpoint not available, trying alternative methods...')
      }

      // If no items found from Method 1, try inventory-based filtering (prioritize for warehouses)
      if (products.length === 0 && rawMaterials.length === 0) {
        try {
          console.log('🔄 Trying inventory-based filtering using service methods...')

          // Use the proper service methods which use query parameters
          const [productInventoryResponse, rawMaterialInventoryResponse] = await Promise.all([
            ProductInventoryService.getInventoriesByBranch(branchId).catch(() => ({ success: false })),
            RawMaterialInventoryService.getInventoriesByBranch(branchId).catch(() => ({ success: false }))
          ])

          if (productInventoryResponse?.success) {
            const inventories = productInventoryResponse.data?.productInventories || []
            products = inventories
              .filter(inv => inv.product && inv.product.isActive !== false)
              .map(inv => inv.product)
              .filter(product => product.type === 'product' || product.itemType === 'product' || !product.type)
            console.log('✅ Extracted', products.length, 'products from branch inventories using service method')
          }

          if (rawMaterialInventoryResponse?.success) {
            const inventories = rawMaterialInventoryResponse.data?.rawMaterialInventories || []
            rawMaterials = inventories
              .filter(inv => inv.rawMaterial && inv.rawMaterial.isActive !== false)
              .map(inv => inv.rawMaterial)
            console.log('✅ Extracted', rawMaterials.length, 'raw materials from branch inventories using service method')
          }
        } catch (error) {
          console.log('⚠️ Inventory-based filtering failed, trying unassigned products...')

          // Method 3: Try to get unassigned products for outlets (not warehouses)
          try {
            const [unassignedProductsResponse, unassignedRawMaterialsResponse] = await Promise.all([
              ProductInventoryService.getUnassignedProducts(branchId).catch(() => ({ success: false })),
              ApiService.get(`/raw-material-inventories/unassigned-raw-materials/${branchId}`).catch(() => ({ success: false }))
            ])

            if (unassignedProductsResponse?.success) {
              products = unassignedProductsResponse.data?.products || []
              console.log('✅ Got', products.length, 'unassigned products for branch')
            }

            if (unassignedRawMaterialsResponse?.success) {
              rawMaterials = unassignedRawMaterialsResponse.data?.rawMaterials || []
              console.log('✅ Got', rawMaterials.length, 'unassigned raw materials for branch')
            }
          } catch (error) {
            console.log('⚠️ Unassigned items endpoints also failed, falling back to all items...')
          }
        }
      }

      console.log('✅ Final available items for branch:', products.length, 'products and', rawMaterials.length, 'raw materials')

      setAvailableItems({
        rawMaterials,
        products
      })
    } catch (error) {
      console.error('❌ Error fetching branch-specific items:', error)
      setAvailableItems({
        rawMaterials: [],
        products: []
      })
    } finally {
      setItemsLoading(false)
    }
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }

    // Clear supplier/warehouse when type changes
    if (name === 'supplierType') {
      setFormData(prev => ({
        ...prev,
        supplier: '',
        warehouse: ''
      }))
    }
  }

  // Handle item selection for multiple selection
  const handleItemSelection = (item, itemType) => {
    const itemKey = `${itemType}_${item._id}`

    // Check if item is already in the order
    const existingItem = formData.orderItems.find(orderItem =>
      (itemType === 'product' && orderItem.productId === item._id) ||
      (itemType === 'raw_material' && orderItem.rawMaterialId === item._id)
    )

    if (existingItem) {
      setSelectorError(`"${item.name}" is already in the order. You can update the quantity instead.`)
      return
    }

    // Clear error when selecting
    setSelectorError('')

    // Toggle selection
    setSelectedItems(prev => {
      const isSelected = prev.some(selected => selected.key === itemKey)
      if (isSelected) {
        return prev.filter(selected => selected.key !== itemKey)
      } else {
        // Validate product type before adding
        if (itemType === 'product') {
          const isEligibleProduct = item.type === 'product' || item.itemType === 'product' || !item.type
          if (!isEligibleProduct) {
            setSelectorError(`Product "${item.name}" is not eligible for purchase orders. Only products of type "product" are allowed.`)
            return prev
          }
        }

        return [...prev, { key: itemKey, item, itemType }]
      }
    })
  }

  // Add selected items to order
  const addSelectedItemsToOrder = () => {
    if (selectedItems.length === 0) {
      setSelectorError('Please select at least one item to add.')
      return
    }

    const newItems = selectedItems.map(({ item, itemType }) => ({
      id: PurchaseOrderUtils.generateTempId(),
      itemType,
      productId: itemType === 'product' ? item._id : '',
      rawMaterialId: itemType === 'raw_material' ? item._id : '',
      quantity: 1,
      name: item.name || 'Unknown Item',
      code: item.sku || item.code || 'N/A',
      unit: item.unit?.abbreviation || item.unit?.name || 'unit'
    }))

    console.log('✅ Adding multiple items to order:', newItems.map(item => item.name))

    setFormData(prev => ({
      ...prev,
      orderItems: [...prev.orderItems, ...newItems]
    }))

    // Clear any previous errors and close selector
    setErrors({})
    setShowItemSelector(false)
    setSelectedItems([])
    setSelectorError('')
  }

  // Update order item quantity - allows temporary empty values
  const updateItemQuantity = (itemId, quantity) => {
    setFormData(prev => ({
      ...prev,
      orderItems: prev.orderItems.map(item =>
        item.id === itemId ? { ...item, quantity: quantity } : item
      )
    }))
  }

  // Handle quantity validation when user finishes editing
  const handleQuantityBlur = (itemId, quantity) => {
    const finalQuantity = Math.max(1, parseInt(quantity) || 1)
    setFormData(prev => ({
      ...prev,
      orderItems: prev.orderItems.map(item =>
        item.id === itemId ? { ...item, quantity: finalQuantity } : item
      )
    }))
  }

  // Remove item from order
  const removeItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      orderItems: prev.orderItems.filter(item => item.id !== itemId)
    }))
  }

  // Validate form
  const validateForm = () => {
    const validation = PurchaseOrderModel.validate(formData)
    setErrors(validation.errors)
    return validation.isValid
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      console.log('❌ Form validation failed:', errors)
      return
    }

    try {
      setLoading(true)

      // Use different formatting for create vs update
      let apiData
      if (mode === 'edit' && editOrder) {
        // For updates, use formatForUpdate which excludes orderDate
        apiData = PurchaseOrderModel.formatForUpdate(formData)
        console.log('🔄 Formatted data for UPDATE (excludes orderDate):', apiData)
      } else {
        // For creates, use formatForAPI which includes all fields
        apiData = PurchaseOrderModel.formatForAPI(formData)
        console.log('🔄 Formatted data for CREATE:', apiData)
      }

      let response
      if (mode === 'edit' && editOrder) {
        response = await PurchaseOrderService.updatePurchaseOrder(editOrder._id, apiData)
      } else {
        response = await PurchaseOrderService.createPurchaseOrder(apiData)
      }

      if (response?.success) {
        onOrderCreated?.(response.data.purchaseOrder)
        onClose()
      }
    } catch (error) {
      console.error(`❌ Error ${mode === 'edit' ? 'updating' : 'creating'} purchase order:`, error)
      setErrors({ submit: error.message })
    } finally {
      setLoading(false)
    }
  }

  // Filter available items based on search
  const filteredItems = () => {
    const items = selectedItemType === 'product' ? availableItems.products : availableItems.rawMaterials
    return PurchaseOrderUtils.filterItems(items, itemSearchTerm, ['name', 'sku', 'code'])
  }

  // Check if item is already selected
  const isItemSelected = (item, itemType) => {
    const itemKey = `${itemType}_${item._id}`
    return selectedItems.some(selected => selected.key === itemKey)
  }

  // Check if item is already in order
  const isItemInOrder = (item, itemType) => {
    return formData.orderItems.some(orderItem =>
      (itemType === 'product' && orderItem.productId === item._id) ||
      (itemType === 'raw_material' && orderItem.rawMaterialId === item._id)
    )
  }

  // Check if Add Item button should be disabled
  const isAddItemDisabled = () => {
    const sourceBranchId = getItemSourceBranchId()
    return !sourceBranchId || itemsLoading || (availableItems.products.length === 0 && availableItems.rawMaterials.length === 0)
  }

  // Get current item source description
  const getItemSourceDescription = () => {
    if (formData.supplierType === 'warehouse' && formData.warehouse) {
      const warehouseName = warehouses.find(w => w._id === formData.warehouse)?.name
      return `Items from ${warehouseName || 'selected warehouse'}`
    } else if (formData.requestingBranch) {
      const branchName = branches.find(b => b._id === formData.requestingBranch)?.name
      return `Items from ${branchName || 'requesting branch'}`
    }
    return 'No item source selected'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center space-x-3">
            {/* <ShoppingCart className="h-6 w-6 text-orange-600" /> */}
            <h2 className="text-xl font-semibold text-gray-900">
              {mode === 'edit' ? 'Edit Purchase Order' : 'Create Purchase Order'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex min-h-0">
          {/* Left Panel - Order Details */}
          <div className="w-1/2 p-6 border-r border-gray-200 overflow-y-auto max-h-[calc(90vh-120px)]">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Purchase Order Details</h3>

            {/* PO Number (for edit mode) */}
            {mode === 'edit' && editOrder?.poNumber && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PO Number
                </label>
                <Input
                  value={editOrder.poNumber}
                  disabled
                  className="bg-gray-50"
                />
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Requesting Branch *
              </label>
              <div className="relative">
                <select
                  name="requestingBranch"
                  value={formData.requestingBranch}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none"
                  disabled={dropdownsLoading}
                >
                  <option value="">Select Requesting Branch</option>
                  {branches.map(branch => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name} {branch.type === 'warehouse' ? '(Warehouse)' : '(Outlet)'}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {errors.requestingBranch && (
                <p className="mt-1 text-sm text-red-600">{errors.requestingBranch}</p>
              )}

              {/* Branch Type Restriction Info */}
              {isRequestingBranchWarehouse() && (
                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-md">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 text-orange-600 mr-2" />
                    <p className="text-sm text-orange-800">
                      Since this is a warehouse branch, you can only order from suppliers.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Supplier Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supplier Type *
              </label>
              <div className="relative">
                <select
                  name="supplierType"
                  value={formData.supplierType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none"
                  disabled={isRequestingBranchWarehouse()}
                >
                  {getAvailableSupplierTypes().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {formData.supplierType === 'supplier' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supplier *
                </label>
                <div className="relative">
                  <select
                    name="supplier"
                    value={formData.supplier}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none"
                    disabled={dropdownsLoading}
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map(supplier => (
                      <option key={supplier._id} value={supplier._id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                {errors.supplier && (
                  <p className="mt-1 text-sm text-red-600">{errors.supplier}</p>
                )}
              </div>
            )}

            {/* Warehouse Selection */}
            {formData.supplierType === 'warehouse' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Warehouse *
                  <span className="text-xs text-orange-600 ml-2">(Items will be sourced from this warehouse)</span>
                </label>
                <select
                  name="warehouse"
                  value={formData.warehouse}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={dropdownsLoading}
                >
                  <option value="">Select Warehouse</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse._id} value={warehouse._id}>
                      {warehouse.name}
                    </option>
                  ))}
                </select>
                {errors.warehouse && (
                  <p className="mt-1 text-sm text-red-600">{errors.warehouse}</p>
                )}
              </div>
            )}

            {/* Order Date - Show as disabled in edit mode */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order Date *
                {mode === 'edit' && (
                  <span className="text-xs text-gray-500 ml-2">(Cannot be modified)</span>
                )}
              </label>
              <Input
                type="date"
                name="orderDate"
                value={formData.orderDate}
                onChange={handleInputChange}
                className={`${errors.orderDate ? 'border-red-500' : ''} ${mode === 'edit' ? 'bg-gray-50' : ''}`}
                disabled={mode === 'edit'} // Disable in edit mode since API doesn't allow updates
              />
              {errors.orderDate && (
                <p className="mt-1 text-sm text-red-600">{errors.orderDate}</p>
              )}
            </div>

            {/* Expected Delivery Date */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Delivery Date *
              </label>
              <Input
                type="date"
                name="expectedDeliveryDate"
                value={formData.expectedDeliveryDate}
                onChange={handleInputChange}
                className={errors.expectedDeliveryDate ? 'border-red-500' : ''}
              />
              {errors.expectedDeliveryDate && (
                <p className="mt-1 text-sm text-red-600">{errors.expectedDeliveryDate}</p>
              )}
            </div>

            {/* Notes */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Additional notes or special instructions..."
              />
            </div>
          </div>

          {/* Right Panel - Item Details */}
          <div className="w-1/2 p-6 flex flex-col max-h-[calc(90vh-120px)]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Item Details</h3>
                <p className="text-sm text-gray-600">{getItemSourceDescription()}</p>
              </div>
              <div className="flex items-center space-x-3">
                {itemsLoading && (
                  <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
                )}
                <Button
                  type="button"
                  onClick={() => setShowItemSelector(true)}
                  disabled={isAddItemDisabled()}
                  className={`flex items-center space-x-2 ${isAddItemDisabled() ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={!getItemSourceBranchId() ? 'Please complete the form to select items' : 'Add items'}
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Item</span>
                </Button>
              </div>
            </div>

            {/* Item Source Status */}
            {!getItemSourceBranchId() && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Complete the form to see available items:</strong><br />
                  {!formData.requestingBranch && '• Select a requesting branch'}
                  {formData.requestingBranch && formData.supplierType === 'warehouse' && !formData.warehouse && '• Select a warehouse'}
                </p>
              </div>
            )}

            {/* Items Loading Status */}
            {getItemSourceBranchId() && itemsLoading && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800 flex items-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading items from {formData.supplierType === 'warehouse' ? 'warehouse' : 'branch'}...
                </p>
              </div>
            )}

            {/* No Items Available */}
            {getItemSourceBranchId() && !itemsLoading && availableItems.products.length === 0 && availableItems.rawMaterials.length === 0 && (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600">
                  No items available from the selected {formData.supplierType === 'warehouse' ? 'warehouse' : 'branch'}.
                  Please check the inventory or contact your administrator.
                </p>
              </div>
            )}

            {/* Order Items List - Scrollable */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-2 min-h-0">
              {formData.orderItems.map((item, index) => (
                <div key={item.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">{item.name}</span>
                        <span className="text-sm text-gray-500">{item.code}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${item.itemType === 'product'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-green-100 text-green-800'
                          }`}>
                          {item.itemType === 'product' ? 'Product' : 'Raw Material'}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Quantity
                      </label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItemQuantity(item.id, e.target.value)}
                        onBlur={(e) => handleQuantityBlur(item.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleQuantityBlur(item.id, e.target.value)
                            e.target.blur() // Remove focus after Enter
                          }
                        }}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Unit
                      </label>
                      <Input
                        value={item.unit}
                        disabled
                        className="text-sm bg-gray-100"
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Empty State */}
              {formData.orderItems.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No items added yet</p>
                  <p className="text-sm">
                    {!getItemSourceBranchId()
                      ? 'Complete the form first, then click "Add Item" to start building your purchase order'
                      : 'Click "Add Item" to start building your purchase order'
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Fixed Footer Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              {/* Items Summary */}
              {formData.orderItems.length > 0 && (
                <div className="mb-4 p-3 bg-orange-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Total Items:</span>
                    <span className="text-lg font-bold text-orange-600">
                      {formData.orderItems.length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm font-medium text-gray-700">Total Quantity:</span>
                    <span className="text-lg font-bold text-green-600">
                      {formData.orderItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)}
                    </span>
                  </div>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || formData.orderItems.length === 0}
                  className="flex items-center space-x-2"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  <span>{mode === 'edit' ? 'Update' : 'Create'} Purchase Order</span>
                </Button>
              </div>

              {errors.submit && (
                <p className="mt-2 text-sm text-red-600 text-center">{errors.submit}</p>
              )}
            </div>
          </div>
        </form>

        {/* Item Selector Modal */}
        {showItemSelector && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Select Items
                    <span className="text-sm font-normal text-orange-600 ml-2">
                      ({getItemSourceDescription()})
                    </span>
                  </h3>
                  <button
                    onClick={() => setShowItemSelector(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Item Type Selector */}
                <div className="flex space-x-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setSelectedItemType('product')}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${selectedItemType === 'product'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Products ({availableItems.products.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedItemType('raw_material')}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${selectedItemType === 'raw_material'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Raw Materials ({availableItems.rawMaterials.length})
                  </button>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    type="text"
                    placeholder="Search items..."
                    value={itemSearchTerm}
                    onChange={(e) => setItemSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Error Display */}
                {selectorError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{selectorError}</p>
                  </div>
                )}
              </div>

              {/* Items List */}
              <div className="p-4 max-h-64 overflow-y-auto">
                <div className="space-y-2">
                  {filteredItems().map(item => {
                    const isSelected = isItemSelected(item, selectedItemType)
                    const isInOrder = isItemInOrder(item, selectedItemType)

                    return (
                      <div
                        key={item._id}
                        onClick={() => !isInOrder && handleItemSelection(item, selectedItemType)}
                        className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${isInOrder
                            ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                            : isSelected
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isInOrder
                              ? 'border-gray-300 bg-gray-200'
                              : isSelected
                                ? 'border-orange-500 bg-orange-500'
                                : 'border-gray-300'
                            }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                            {isInOrder && <span className="text-xs text-gray-400">✓</span>}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{item.name || 'Unknown Item'}</h4>
                            <p className="text-sm text-gray-500">
                              {item.sku || item.code || 'N/A'} • {item.unit?.name || item.unit?.abbreviation || 'unit'}
                            </p>
                          </div>
                        </div>
                        {isInOrder && (
                          <span className="text-xs text-gray-500 font-medium">Already Added</span>
                        )}
                      </div>
                    )
                  })}
                </div>

                {filteredItems().length === 0 && (
                  <p className="text-center text-gray-500 py-8">
                    No items found in the selected {formData.supplierType === 'warehouse' ? 'warehouse' : 'branch'}
                  </p>
                )}
              </div>

              {/* Selector Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
                  </span>
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowItemSelector(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={addSelectedItemsToOrder}
                      disabled={selectedItems.length === 0}
                      className="flex items-center space-x-2"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add {selectedItems.length} Item{selectedItems.length !== 1 ? 's' : ''}</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CreatePurchaseOrderModal